import { create } from "zustand";
import { customerService, expenseService } from "../services";
import { CustomerT, newCustomerT } from "../types";
import { debounce } from "lodash";
import { ICustomer, IPaginationMeta } from "../@types";
import { createCustomer } from "../backend-services";

interface FetchOptions {
  startDate?: string;
  endDate?: string;
  actionType?: "initial" | "filter";
}

interface CustomerStore {
  customers: CustomerT[];
  singleCustomer: CustomerT;
  hasData: boolean;
  newCustomers: CustomerT[];
  isLoading: boolean;
  isLoadingInitial: boolean;
  isLoadingFilter: boolean;
  isLoadingMore: boolean;
  pagination: IPaginationMeta;
  error: boolean;
  range: string;
  fetchCustomers: () => Promise<void>;
  fetchData: (options?: FetchOptions) => Promise<void> | undefined;
  fetchMoreData: () => Promise<void>;
  createCustomer: (customer: any) => Promise<void>;
  setSearchText: (text: string) => void;
  getCustomerById: (id: string) => Promise<ICustomer | null>;
  searchText: string;
}

export const useCustomerStore = create<CustomerStore>((set, get) => ({
  customers: [],
  singleCustomer: {} as CustomerT,
  hasData: false,
  newCustomers: [],
  isLoading: false,
  isLoadingInitial: true,
  isLoadingFilter: false,
  isLoadingMore: false,
  error: false,
  range: "",
  searchText: "",
  pagination: {
    cursor: null,
    backCursor: null,
    hasMoreData: false,
    limit: undefined,
    count: undefined,
  },
  handleSort: (text: string) => {
    set({ range: text });
  },

  fetchCustomers: async () => {
    set({ isLoading: true });
    try {
      const res = await customerService.get("/customers");
      set({ customers: res.data.data, isLoading: false });
    } catch (e: any) {
      set((state) => ({ isLoading: false, error: e.message }));
    }
  },
  createCustomer: async (newCustomerData: any) => {
    set({ isLoading: true });
    try {
      const res = await createCustomer(newCustomerData);
      const newCustomer = res;

      set((state) => ({
        customers: [newCustomer, ...state.customers],
        newCustomers: [newCustomer, ...state.newCustomers],
        isLoading: false,
      }));
    } catch (e: any) {
      set({ isLoading: false, error: e.message });
    }
  },

  fetchData: async (options?: FetchOptions) => {
    const { startDate, endDate, actionType = "initial" } = options || {};
    if (actionType === "initial") {
      set({ isLoadingInitial: true });
    } else if (actionType === "filter") {
      set({ isLoadingFilter: true });
    }
    set({ isLoading: true });
    const { searchText, range, newCustomers } = get();
    try {
      let query = `/customers`;

      if (searchText.length > 0) {
        query += `/search?q=${searchText}`;
      } else {
        if (startDate && endDate) {
          query += `?startDate=${startDate}&endDate=${endDate}`;
        }
        if (searchText && searchText.length > 0)
          query += `search=${searchText}&`;
        if (range.length > 0) query += `range=${range.toLowerCase()}&`;
      }
      const res = await customerService.get(query);

      let combinedCustomers = res.data.data;

      if (searchText.length == 0 && combinedCustomers.length > 0) {
        set({ hasData: true });
      }
      set({
        customers: searchText.length > 0 ? res.data : combinedCustomers,
        pagination: res.data.meta,
        isLoading: false,
        isLoadingInitial: false,
        isLoadingFilter: false,
      });
    } catch (e: any) {
      set({
        isLoading: false,
        error: e.message,
        isLoadingInitial: false,
        isLoadingFilter: false,
      });
    }
  },

  fetchMoreData: async () => {
    if (!get()?.pagination?.hasMoreData || get()?.isLoadingMore) return;
    set({ isLoadingMore: true });
    const cursor = get().pagination.cursor;
    try {
      const res = await customerService.get(`/customers?cursor=${cursor}`);
      set((state) => ({
        customers: [...state.customers, ...res.data.data],
        pagination: res.data.meta,
        isLoadingMore: false,
      }));
    } catch (e: any) {
      set({ isLoadingMore: false, error: e.message });
    }
  },
  setSearchText: (text: string) => {
    set({ searchText: text });
  },
  getCustomerById: async (id: string): Promise<ICustomer | null> => {
    set({ isLoading: true });
    try {
      const res = await customerService.get(`/customers/${id}`);

      set({ isLoading: false, singleCustomer: res.data });
      return res.data;
    } catch (e: any) {
      console.error(e.response.data);
      set({ isLoading: false, error: e.message });
      return null;
    }
  },
}));
